import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Alert,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Chip,
} from "@mui/material";
import {
    LocalShipping,
    CheckCircle,
    Cancel,
    Pending,
    Assignment,
    EditNote,
} from "@mui/icons-material";
import { Order } from "../../types";
import { orderService } from "../../services/orderService";
import { telegramService } from "../../services/telegramService";

interface OrderStatusManagerProps {
    open: boolean;
    onClose: () => void;
    order: Order | null;
    onSuccess: () => void;
    onDeliveredUnpaid?: (order: Order) => void;
}

const statusSteps = ["PENDING", "CONFIRMED", "SHIPPED", "DELIVERED"];

export const getStatusIcon = (status: string) => {
    switch (status) {
        case "DRAFT":
            return <EditNote />;
        case "PENDING":
            return <Pending />;
        case "CONFIRMED":
            return <Assignment />;
        case "SHIPPED":
            return <LocalShipping />;
        case "DELIVERED":
            return <CheckCircle />;
        case "CANCELLED":
            return <Cancel />;
        default:
            return <Pending />;
    }
};

export const getStatusColor = (status: string) => {
    switch (status) {
        case "DRAFT":
            return "default";
        case "PENDING":
            return "warning";
        case "CONFIRMED":
            return "info";
        case "SHIPPED":
            return "primary";
        case "DELIVERED":
            return "success";
        case "CANCELLED":
            return "error";
        default:
            return "default";
    }
};

const getStatusDescription = (status: string) => {
    switch (status) {
        case "DRAFT":
            return "Draft — nothing has been reserved yet; finalize it to commit stock";
        case "PENDING":
            return "Order has been placed and is awaiting confirmation";
        case "CONFIRMED":
            return "Order has been confirmed and is being prepared";
        case "SHIPPED":
            return "Order has been shipped and is on its way";
        case "DELIVERED":
            return "Order has been delivered to the customer";
        case "CANCELLED":
            return "Order has been cancelled";
        default:
            return "";
    }
};

export const OrderStatusManager: React.FC<OrderStatusManagerProps> = ({
    open,
    onClose,
    order,
    onSuccess,
    onDeliveredUnpaid,
}) => {
    const [newStatus, setNewStatus] = useState<string>("");
    const [notes, setNotes] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    React.useEffect(() => {
        if (order) {
            setNewStatus(order.status);
            setNotes("");
            setError("");
        }
    }, [order]);

    const handleStatusUpdate = async () => {
        if (!order || !newStatus) return;

        try {
            setLoading(true);
            setError("");

            const oldStatus = order.status;
            await orderService.updateOrderStatus(order.order_id, newStatus);

            // Send automatic Telegram notification if status changed from PENDING to CONFIRMED or higher
            if (
                oldStatus === "PENDING" &&
                newStatus !== "PENDING" &&
                newStatus !== "CANCELLED"
            ) {
                try {
                    // Fetch the full order details for Telegram notification
                    const fullOrder = await orderService.getOrderForReceipt(
                        order.order_id,
                    );
                    await telegramService.sendOrderNotificationIfNeeded(
                        fullOrder,
                        oldStatus,
                    );
                } catch (telegramError) {
                    console.error(
                        "Failed to send automatic Telegram notification:",
                        telegramError,
                    );
                    // Don't show error to user - automatic notification failure shouldn't block the status update
                }
            }

            const amountPaid = (order.payment_transactions || []).reduce(
                (sum, payment: any) => sum + Number(payment.amount || 0),
                0,
            );
            const totalAmount = Number(order.total_amount || 0);
            const isUnderpaid = amountPaid < totalAmount;

            onSuccess();

            if (newStatus === "DELIVERED" && isUnderpaid && onDeliveredUnpaid) {
                onDeliveredUnpaid(order);
                onClose();
                return;
            }

            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to update order status",
            );
        } finally {
            setLoading(false);
        }
    };

    const getAvailableStatuses = () => {
        if (!order) return [];

        const currentStatus = order.status;

        // DRAFT sits before the fulfillment pipeline, not inside it — it
        // isn't in flowStatuses below, so without this branch indexOf would
        // return -1 and fall through to `[currentStatus]`, leaving a draft
        // with no forward transition at all.
        if (currentStatus === "DRAFT") {
            return ["DRAFT", "PENDING", "CANCELLED"];
        }

        const flowStatuses = ["PENDING", "CONFIRMED", "SHIPPED", "DELIVERED"];
        const currentIndex = flowStatuses.indexOf(currentStatus);

        if (currentIndex === -1) {
            return [currentStatus];
        }

        const upcomingStatuses = flowStatuses.slice(currentIndex + 1);
        return [currentStatus, ...upcomingStatuses, "CANCELLED"];
    };

    const getCurrentStepIndex = () => {
        if (!order) return 0;
        return statusSteps.indexOf(order.status);
    };

    const isStatusChangeValid = () => {
        if (!order || !newStatus) return false;
        return getAvailableStatuses().includes(newStatus);
    };

    const getStatusChangeWarning = () => {
        if (!order || !newStatus) return null;

        if (order.status === "DRAFT") {
            return newStatus === "CANCELLED"
                ? {
                      severity: "info" as const,
                      message:
                          "This draft never reserved any stock, so cancelling it has no inventory impact.",
                  }
                : {
                      severity: "info" as const,
                      message:
                          "Finalizing this draft will reserve stock for its items — it will fail if stock has run out since it was written down.",
                  };
        }

        if (newStatus === "CANCELLED") {
            return {
                severity: "warning" as const,
                message:
                    "Cancelling this order will release any reserved stock back to inventory.",
            };
        }

        if (newStatus === "DELIVERED" && order.status !== "SHIPPED") {
            return {
                severity: "warning" as const,
                message:
                    "Marking as delivered will convert reserved stock to sold and cannot be undone.",
            };
        }

        if (newStatus === "SHIPPED" && order.status === "PENDING") {
            return {
                severity: "info" as const,
                message: "Consider confirming the order first before shipping.",
            };
        }

        return null;
    };

    if (!order) return null;

    const warning = getStatusChangeWarning();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {getStatusIcon(order.status)}
                    <Box>
                        <Typography variant="h6">
                            Update Order Status
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Order #{order.order_number}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Current Status */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Current Status
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status}
                            color={getStatusColor(order.status) as any}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {getStatusDescription(order.status)}
                        </Typography>
                    </Box>
                </Box>

                {/* Status Progress */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Order Progress
                    </Typography>
                    <Stepper
                        activeStep={getCurrentStepIndex()}
                        alternativeLabel
                    >
                        {statusSteps.map((status, index) => (
                            <Step
                                key={status}
                                completed={
                                    index < getCurrentStepIndex() ||
                                    order.status === "DELIVERED"
                                }
                            >
                                <StepLabel
                                    StepIconComponent={() =>
                                        getStatusIcon(status)
                                    }
                                >
                                    {status}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* New Status Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>New Status</InputLabel>
                    <Select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        label="New Status"
                    >
                        {getAvailableStatuses().map((status) => (
                            <MenuItem key={status} value={status}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    {getStatusIcon(status)}
                                    {status}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Status Change Preview */}
                {newStatus && newStatus !== order.status && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Preview
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Chip
                                icon={getStatusIcon(order.status)}
                                label={order.status}
                                color={getStatusColor(order.status) as any}
                                size="small"
                            />
                            <Typography>→</Typography>
                            <Chip
                                icon={getStatusIcon(newStatus)}
                                label={newStatus}
                                color={getStatusColor(newStatus) as any}
                                size="small"
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {getStatusDescription(newStatus)}
                        </Typography>
                    </Box>
                )}

                {/* Warning Messages */}
                {warning && (
                    <Alert severity={warning.severity} sx={{ mb: 2 }}>
                        {warning.message}
                    </Alert>
                )}

                {/* Notes */}
                <TextField
                    fullWidth
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Add any notes about this status change..."
                    sx={{ mb: 2 }}
                />

                {/* Order Details Summary */}
                <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Order Summary
                    </Typography>
                    <Typography variant="body2">
                        Customer:{" "}
                        {order.customer
                            ? `${order.customer.first_name} ${order.customer.last_name}`
                            : "N/A"}
                    </Typography>
                    <Typography variant="body2">
                        Total: ৳{Number(order.total_amount).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                        Order Date:{" "}
                        {new Date(order.order_date).toLocaleDateString()}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleStatusUpdate}
                    variant="contained"
                    disabled={
                        loading ||
                        !isStatusChangeValid() ||
                        newStatus === order.status
                    }
                >
                    {loading ? "Updating..." : "Update Status"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
